import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isToday from 'dayjs/plugin/isToday';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
export * from './chat-message';
export * from './local-storage';
export * from './dayPartWindow';

export const getNameInitials = (fullName: string): string => {
  const fInitial = Array.from(fullName.split(' ')[0])[0],
    lInitial = Array.from(fullName.split(' ')[1])[0];

  return `${fInitial}${lInitial}`;
};

export const colourNameToHex = (color: string): string => {
  const colours: any = {
    aliceblue: '#f0f8ff',
    antiquewhite: '#faebd7',
    aqua: '#00ffff',
    aquamarine: '#7fffd4',
    azure: '#f0ffff',
    beige: '#f5f5dc',
    bisque: '#ffe4c4',
    black: '#000000',
    blanchedalmond: '#ffebcd',
    blue: '#0000ff',
    blueviolet: '#8a2be2',
    brown: '#a52a2a',
    burlywood: '#deb887',
    cadetblue: '#5f9ea0',
    chartreuse: '#7fff00',
    chocolate: '#d2691e',
    coral: '#ff7f50',
    cornflowerblue: '#6495ed',
    cornsilk: '#fff8dc',
    crimson: '#dc143c',
    cyan: '#00ffff',
    darkblue: '#00008b',
    darkcyan: '#008b8b',
    darkgoldenrod: '#b8860b',
    darkgray: '#a9a9a9',
    darkgreen: '#006400',
    darkkhaki: '#bdb76b',
    darkmagenta: '#8b008b',
    darkolivegreen: '#556b2f',
    darkorange: '#ff8c00',
    darkorchid: '#9932cc',
    darkred: '#8b0000',
    darksalmon: '#e9967a',
    darkseagreen: '#8fbc8f',
    darkslateblue: '#483d8b',
    darkslategray: '#2f4f4f',
    darkturquoise: '#00ced1',
    darkviolet: '#9400d3',
    deeppink: '#ff1493',
    deepskyblue: '#00bfff',
    dimgray: '#696969',
    dodgerblue: '#1e90ff',
    firebrick: '#b22222',
    floralwhite: '#fffaf0',
    forestgreen: '#228b22',
    fuchsia: '#ff00ff',
    gainsboro: '#dcdcdc',
    ghostwhite: '#f8f8ff',
    gold: '#ffd700',
    goldenrod: '#daa520',
    gray: '#808080',
    green: '#008000',
    greenyellow: '#adff2f',
    honeydew: '#f0fff0',
    hotpink: '#ff69b4',
    'indianred ': '#cd5c5c',
    indigo: '#4b0082',
    ivory: '#fffff0',
    khaki: '#f0e68c',
    lavender: '#e6e6fa',
    lavenderblush: '#fff0f5',
    lawngreen: '#7cfc00',
    lemonchiffon: '#fffacd',
    lightblue: '#add8e6',
    lightcoral: '#f08080',
    lightcyan: '#e0ffff',
    lightgoldenrodyellow: '#fafad2',
    lightgrey: '#d3d3d3',
    lightgreen: '#90ee90',
    lightpink: '#ffb6c1',
    lightsalmon: '#ffa07a',
    lightseagreen: '#20b2aa',
    lightskyblue: '#87cefa',
    lightslategray: '#778899',
    lightsteelblue: '#b0c4de',
    lightyellow: '#ffffe0',
    lime: '#00ff00',
    limegreen: '#32cd32',
    linen: '#faf0e6',
    magenta: '#ff00ff',
    maroon: '#800000',
    mediumaquamarine: '#66cdaa',
    mediumblue: '#0000cd',
    mediumorchid: '#ba55d3',
    mediumpurple: '#9370d8',
    mediumseagreen: '#3cb371',
    mediumslateblue: '#7b68ee',
    mediumspringgreen: '#00fa9a',
    mediumturquoise: '#48d1cc',
    mediumvioletred: '#c71585',
    midnightblue: '#191970',
    mintcream: '#f5fffa',
    mistyrose: '#ffe4e1',
    moccasin: '#ffe4b5',
    navajowhite: '#ffdead',
    navy: '#000080',
    oldlace: '#fdf5e6',
    olive: '#808000',
    olivedrab: '#6b8e23',
    orange: '#ffa500',
    orangered: '#ff4500',
    orchid: '#da70d6',
    palegoldenrod: '#eee8aa',
    palegreen: '#98fb98',
    paleturquoise: '#afeeee',
    palevioletred: '#d87093',
    papayawhip: '#ffefd5',
    peachpuff: '#ffdab9',
    peru: '#cd853f',
    pink: '#ffc0cb',
    plum: '#dda0dd',
    powderblue: '#b0e0e6',
    purple: '#800080',
    rebeccapurple: '#663399',
    red: '#ff0000',
    rosybrown: '#bc8f8f',
    royalblue: '#4169e1',
    saddlebrown: '#8b4513',
    salmon: '#fa8072',
    sandybrown: '#f4a460',
    seagreen: '#2e8b57',
    seashell: '#fff5ee',
    sienna: '#a0522d',
    silver: '#c0c0c0',
    skyblue: '#87ceeb',
    slateblue: '#6a5acd',
    slategray: '#708090',
    snow: '#fffafa',
    springgreen: '#00ff7f',
    steelblue: '#4682b4',
    tan: '#d2b48c',
    teal: '#008080',
    thistle: '#d8bfd8',
    tomato: '#ff6347',
    turquoise: '#40e0d0',
    violet: '#ee82ee',
    wheat: '#f5deb3',
    white: '#ffffff',
    whitesmoke: '#f5f5f5',
    yellow: '#ffff00',
    yellowgreen: '#9acd32',
  };

  if (typeof colours[color.toLowerCase()] != 'undefined')
    return colours[color.toLowerCase()];

  return '#000';
};

export const isColorLight = (color: string): boolean => {
  const hex = color.replace('#', '');
  const c_r = parseInt(hex.substr(0, 2), 16);
  const c_g = parseInt(hex.substr(2, 2), 16);
  const c_b = parseInt(hex.substr(4, 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
};

export const goToTop = () => {
  globalThis.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const numberWithCommas = (x: number) => {
  return new Intl.NumberFormat('en-US').format(x);
};



export const formatChatTime = (timeString?: string) => {
  if (!timeString) return '';

  // Parse timeString with timezone Asia/Ho_Chi_Minh
  const date = dayjs(timeString).tz('Asia/Ho_Chi_Minh');

  if (!date.isValid()) return timeString;

  // Format: HH:mm
  return date.format('HH:mm');
};

export const formatChatDate = (timeString?: string) => {
  if (!timeString) return '';
  const date = dayjs(timeString).tz('Asia/Ho_Chi_Minh');
  if (!date.isValid()) return '';

  return date.format('DD/MM/YYYY');
};




export const hasMarkdownSyntax = (text: string): boolean => {
  // **bold** - clearly markdown
  if (/\*\*.*?\*\*/.test(text)) {
    return true;
  }
  // `code` - clearly markdown
  if (/`.*?`/.test(text)) {
    return true;
  }
  // # heading (at start of line)
  if (/^#{1,6}\s/m.test(text)) {
    return true;
  }
  // * list or - list (at start of line, followed by space)
  if (/^[\*\-]\s/m.test(text)) {
    return true;
  }
  // [link](url) or ![image](url)
  // Simplified regex completely free from any backtracking risks.
  // It checks for presence of [ then ]( and then ).
  if (text.includes('](') && text.includes('[') && text.includes(')')) {
    return true;
  }
  return false;
};

export const getNumberSuggestions = (text: string) => {
  const match = text.match(/(?:^|\s)(\d+)$/);

  if (!match) return null;

  const numStr = match[1];
  const startIndex = match.index! + (match[0].length - numStr.length);

  const num = parseInt(numStr, 10);
  if (isNaN(num) || num === 0) return null;

  const suggestionsList: number[] = [];
  const MAX_LIMIT = 999999999;

  let multiplier = 1;
  while (true) {
    const val = num * multiplier;
    if (val > MAX_LIMIT) break;

    if (val >= 1000) {
      suggestionsList.push(val);
    }

    multiplier *= 10;
  }

  // Filter out the exact number that user has typed
  // E.g., if user typed 1000, don't suggest 1000, only 10000, 100000, etc.
  const filteredSuggestions = suggestionsList.filter(val => val !== num);

  if (filteredSuggestions.length === 0) return null;

  const suggestions = filteredSuggestions.map(value => ({
    value,
    label: new Intl.NumberFormat('vi-VN').format(value),
  }));

  return {
    suggestions,
    startIndex,
    endIndex: startIndex + numStr.length
  };
};

export type KeywordSuggestionMode = 'INSTRUCTION' | 'QUICK_FUNCTION';

/**
 * HDSD: bắt đầu bằng ? hoặc tiền tố gõ nhanh huo|hưo|huơ|hươ|hướ (không phân biệt hoa thường).
 * Mở nhanh: bắt đầu bằng > hoặc mo|mơ|mở (không phân biệt hoa thường).
 */
export const parseKeywordSuggestionInput = (
  inputValue: string
): { mode: KeywordSuggestionMode | null; query: string } => {
  if (!inputValue || !inputValue.trim()) {
    return { mode: null, query: '' };
  }
  const instructionMatch = inputValue.match(/^(\?|huo|hư|hưo|huơ|hươ|hướ|hướng dẫn|hướng|huon|huong)(.*)$/i);
  if (instructionMatch) {
    return { mode: 'INSTRUCTION', query: (instructionMatch[2] ?? '').trim() };
  }
  const quickMatch = inputValue.match(/^(\>|mở|mơ|mo)(.*)$/i);
  if (quickMatch) {
    return { mode: 'QUICK_FUNCTION', query: (quickMatch[2] ?? '').trim() };
  }
  return { mode: null, query: '' };
};

export const getQuickFunctionSuggestions = (inputValue: string, quickFunctions: any[] = []): any[] => {
  const { mode, query } = parseKeywordSuggestionInput(inputValue);
  if (mode !== 'QUICK_FUNCTION') return [];
  const normalizedInput = inputValue.toLowerCase().trim();
  const triggerOnly = /^(\>|mở|mơ|mo)$/i.test(inputValue.trim());
  if (triggerOnly) {
    return quickFunctions.filter((item) => Boolean(item?.name)).slice(0, 5);
  }
  return quickFunctions
    .filter((item) => {
      if (!item.name) return false;
      const name = item.name.toLowerCase();
      if (name.startsWith(normalizedInput)) return true;
      if (query && name.startsWith(query.toLowerCase())) return true;
      return false;
    })
    .slice(0, 5);
};

export const getInstructionSuggestions = (inputValue: string, instructions: any[] = []): any[] => {
  const { mode, query } = parseKeywordSuggestionInput(inputValue);
  if (mode !== 'INSTRUCTION') return [];
  const normalizedInput = inputValue.toLowerCase().trim();
  const getLabel = (item: any) =>
    typeof item === 'string'
      ? item
      : (item.function_name || item.question || item.title || item.name || '');
  const triggerOnly = /^(\?|huo|hưo|huơ|hươ|hướ|huon|huong)$/i.test(inputValue.trim());
  if (triggerOnly) {
    return instructions
      .filter((item) => Boolean(getLabel(item)))
      .slice(0, 20);
  }
  return instructions
    .filter((item) => {
      const content = getLabel(item);
      if (!content) return false;
      const lowerContent = content.toLowerCase();
      if (lowerContent.startsWith(normalizedInput)) return true;
      if (query && lowerContent.startsWith(query.toLowerCase())) return true;
      return false;
    })
    .slice(0, 20);
};

export const safePostMessage = (message: any) => {
  if (globalThis.parent && globalThis.parent !== (globalThis as any)) {
    const rawAllowedDomains = process.env.REACT_APP_ALLOWED_DOMAINS?.split(',') || [];
    
    // Process domains to ensure they have the protocol (http/https)
    const allowedOrigins = rawAllowedDomains.map(d => {
      const domain = d.trim();
      if (!domain) return null;
      if (domain.startsWith('http://') || domain.startsWith('https://')) return domain;
      // Default to http for localhost/127.0.0.1, otherwise https
      if (domain.includes('localhost') || domain.includes('127.0.0.1')) return `http://${domain}`;
      return `https://${domain}`;
    }).filter(Boolean);

    if (allowedOrigins.length === 0) {
      console.warn('[safePostMessage] No allowed domains configured in REACT_APP_ALLOWED_DOMAINS. Fallback to origin *.');
      console.log('[safePostMessage] Sending postMessage to parent with fallback origin "*". Payload:', message);
      try {
        globalThis.parent.postMessage(message, '*');
      } catch (e) {
        console.error('[safePostMessage] Post message error for fallback origin *:', e);
      }
      return;
    }

    for (const origin of allowedOrigins) {
      console.log('[safePostMessage] Sending postMessage to parent with allowed origin:', origin, 'Payload:', message);
      try {
        globalThis.parent.postMessage(message, origin!);
      } catch (e) {
        console.error('[safePostMessage] Post message error for origin:', origin, e);
      }
    }
  } else {
    console.warn('[safePostMessage] Not inside an iframe. Cannot send postMessage to parent. Payload:', message);
  }
};

